/**
 * 通用错误处理工具
 */

/**
 * 从API错误中提取用户友好的错误信息
 * @param {Error} error - API错误对象
 * @param {string} defaultMessage - 默认错误信息
 * @returns {string} 用户友好的错误信息
 */
export function extractErrorMessage(error, defaultMessage = '操作失败') {
  // 如果有响应数据，优先使用后端返回的错误信息
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // 如果有验证错误，提取具体的字段错误
  if (error.response?.data?.errors) {
    const errors = error.response.data.errors;
    const errorMessages = Object.values(errors).flat();
    return errorMessages.join(', ');
  }
  
  // 如果有响应数据但没有message字段，尝试其他字段
  if (error.response?.data) {
    const data = error.response.data;
    if (data.error) return data.error;
    if (data.msg) return data.msg;
    if (data.message) return data.message;
  }
  
  // 使用错误对象的message
  if (error.message) {
    return error.message;
  }
  
  // 根据状态码返回通用错误信息
  if (error.response?.status) {
    const status = error.response.status;
    switch (status) {
      case 400:
        return '请求参数错误';
      case 401:
        return '未授权，请重新登录';
      case 403:
        return '权限不足';
      case 404:
        return '请求的资源不存在';
      case 422:
        return '数据验证失败';
      case 429:
        return '请求过于频繁，请稍后再试';
      case 500:
        return '服务器内部错误';
      case 502:
        return '网关错误';
      case 503:
        return '服务暂时不可用';
      default:
        return `请求失败 (${status})`;
    }
  }
  
  // 网络错误
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return '网络连接失败，请检查网络设置';
  }
  
  // 超时错误
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return '请求超时，请稍后重试';
  }
  
  return defaultMessage;
}

/**
 * 从API错误中提取字段特定的错误信息
 * @param {Error} error - API错误对象
 * @param {string} fieldName - 字段名称
 * @returns {string|null} 字段错误信息，如果没有则返回null
 */
export function extractFieldError(error, fieldName) {
  if (error.response?.data?.errors?.[fieldName]) {
    const fieldErrors = error.response.data.errors[fieldName];
    return Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
  }
  return null;
}

/**
 * 检查是否是验证错误
 * @param {Error} error - API错误对象
 * @returns {boolean} 是否是验证错误
 */
export function isValidationError(error) {
  return error.response?.status === 422 || 
         error.response?.status === 400 && error.response?.data?.errors;
}

/**
 * 检查是否是认证错误
 * @param {Error} error - API错误对象
 * @returns {boolean} 是否是认证错误
 */
export function isAuthError(error) {
  return error.response?.status === 401 || error.response?.status === 403;
}

/**
 * 检查是否是网络错误
 * @param {Error} error - API错误对象
 * @returns {boolean} 是否是网络错误
 */
export function isNetworkError(error) {
  return !error.response && (
    error.code === 'NETWORK_ERROR' || 
    error.message?.includes('Network Error') ||
    error.message?.includes('timeout')
  );
}
